/**
 * Nivel de detalle.
 *
 * El zoom NO cambia cuanta gente se ve: las 80 personas estan siempre en
 * pantalla y nada esta plegado. Lo que cambia es cuanto cuenta cada una.
 *
 *   muy alejado -> silueta minima, sin texto, con la forma y el color
 *                  llevando toda la informacion
 *   medio       -> nombre y apellidos
 *   muy cerca   -> nombre y, si hay un punto de perspectiva elegido, el
 *                  parentesco con esa persona ("padre", "prima hermana")
 *
 * Las transiciones son graduales, no saltos: cada capa de texto tiene su
 * propia franja de aparicion y se funde dentro de ella. Por eso la funcion
 * devuelve opacidades continuas y no un modo discreto.
 */

/** Franja en la que aparece el nombre. */
const NOMBRE_DESDE = 0.17;
const NOMBRE_HASTA = 0.29;

/** Franja en la que aparece la tarjeta (el rectangulo de fondo). */
const TARJETA_DESDE = 0.12;
const TARJETA_HASTA = 0.185;

/**
 * Franja en la que aparece la etiqueta de parentesco bajo el nombre.
 *
 * Entra poco despues que el nombre, no mucho mas tarde: es texto corto y es
 * justo lo que se quiere leer al acercarse a una rama ("padre", "prima
 * hermana"). El detalle largo de cada persona vive en la ficha lateral.
 */
const RELACION_DESDE = 0.24;
const RELACION_HASTA = 0.36;

/** Lado que se le quiere dar a la silueta minima, en pixeles de PANTALLA. */
export const LADO_PUNTO_PANTALLA = 9;

export interface Detalle {
  /** Opacidad de la tarjeta de fondo. */
  tarjeta: number;
  /** Opacidad de la silueta minima. */
  punto: number;
  /** Opacidad del nombre. */
  nombre: number;
  /** Opacidad de la etiqueta de parentesco. */
  relacion: number;
  /** Si merece la pena tener creado el texto de parentesco. */
  necesitaRelacion: boolean;
  /** Si merece la pena tener creado el texto del nombre. */
  necesitaNombre: boolean;
}

/** Interpolacion suave entre dos umbrales: sin esquinas en la transicion. */
export function suave(a: number, b: number, v: number): number {
  if (v <= a) return 0;
  if (v >= b) return 1;
  const t = (v - a) / (b - a);
  return t * t * (3 - 2 * t);
}

export function detalleDe(escala: number): Detalle {
  const tarjeta = suave(TARJETA_DESDE, TARJETA_HASTA, escala);
  const nombre = suave(NOMBRE_DESDE, NOMBRE_HASTA, escala);
  const relacion = suave(RELACION_DESDE, RELACION_HASTA, escala);
  return {
    tarjeta,
    // La silueta minima es el complemento de la tarjeta: cuando una entra,
    // la otra sale, y en medio se solapan un poco.
    punto: 1 - tarjeta,
    nombre,
    relacion,
    necesitaNombre: nombre > 0.004,
    necesitaRelacion: relacion > 0.004,
  };
}

/**
 * Escala que hay que aplicar a la silueta minima para que conserve un tamano
 * fijo en pantalla aunque el mundo se aleje.
 *
 * Sin esto, al alejarse los nodos se vuelven invisibles y se pierde justo lo
 * que se queria ver de lejos: la estructura.
 */
export function escalaPunto(escalaCamara: number, ladoTextura: number): number {
  return LADO_PUNTO_PANTALLA / (ladoTextura * Math.max(escalaCamara, 1e-6));
}
